import React, { useState } from 'react';
import {
    Box,
    Typography,
    useMediaQuery,
    Tooltip,
    ClickAwayListener,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const PortfolioInsightsRisk = ({ holdings, riskData = null }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Metric card component
    const MetricCard = ({ label, value, tooltip, color, suffix = '' }) => {
        const [tooltipOpen, setTooltipOpen] = useState(false);

        return (
            <Box sx={{
                p: 2,
                bgcolor: theme.palette.background.default,
                borderRadius: 2,
                textAlign: 'center',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                    >
                        {label}
                    </Typography>
                    <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                        <div>
                            <Tooltip
                                title={tooltip}
                                arrow
                                placement="top"
                                open={tooltipOpen}
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                onClose={() => setTooltipOpen(false)}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: 14,
                                        color: theme.palette.text.secondary,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setTooltipOpen(!tooltipOpen)}
                                />
                            </Tooltip>
                        </div>
                    </ClickAwayListener>
                </Box>
                <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    fontWeight="bold"
                    sx={{ color: color || 'inherit' }}
                >
                    {value}{suffix}
                </Typography>
            </Box>
        );
    };

    // Get color for beta
    const getBetaColor = (beta) => {
        if (beta === null) return theme.palette.text.primary;
        if (beta < 0.8) return theme.palette.info.main;
        if (beta <= 1.2) return theme.palette.success.main;
        return theme.palette.warning.main;
    };

    // Get color for sharpe
    const getSharpeColor = (sharpe) => {
        if (sharpe === null) return theme.palette.text.primary;
        if (sharpe >= 2) return theme.palette.success.main;
        if (sharpe >= 1) return theme.palette.success.light;
        if (sharpe >= 0) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    // Get color for volatility
    const getVolatilityColor = (vol) => {
        if (vol === null) return theme.palette.text.primary;
        if (vol < 15) return theme.palette.success.main;
        if (vol <= 25) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    // Get color for max drawdown
    const getDrawdownColor = (dd) => {
        if (dd === null) return theme.palette.text.primary;
        if (dd < 10) return theme.palette.success.main;
        if (dd <= 20) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    // Get color for alpha
    const getAlphaColor = (alpha) => {
        if (alpha === null) return theme.palette.text.primary;
        if (alpha > 0) return theme.palette.success.main;
        if (alpha < 0) return theme.palette.error.main;
        return theme.palette.text.secondary;
    };

    // Get background color for correlation cell
    const getCorrelationBgColor = (corr) => {
        if (corr === null || corr === undefined) return 'transparent';
        if (corr === 1) return theme.palette.action.hover; // Diagonal
        if (corr >= 0.7) return theme.palette.error.dark + '40'; // Red with opacity
        if (corr >= 0.4) return theme.palette.warning.dark + '40'; // Yellow with opacity
        return theme.palette.success.dark + '40'; // Green with opacity
    };

    // Get text color for correlation
    const getCorrelationTextColor = (corr) => {
        if (corr === null || corr === undefined) return theme.palette.text.primary;
        if (corr === 1) return theme.palette.text.secondary;
        if (corr >= 0.7) return theme.palette.error.main;
        if (corr >= 0.4) return theme.palette.warning.main;
        return theme.palette.success.main;
    };

    // Build matrix data structure from correlation pairs
    const buildCorrelationMatrix = () => {
        if (!riskData?.correlationMatrix || riskData.correlationMatrix.length === 0) {
            return { symbols: [], matrix: {} };
        }

        // Get unique symbols
        const symbolSet = new Set();
        riskData.correlationMatrix.forEach(item => {
            symbolSet.add(item.symbol1);
            symbolSet.add(item.symbol2);
        });
        const symbols = Array.from(symbolSet).sort();

        // Build matrix lookup
        const matrix = {};
        symbols.forEach(s1 => {
            matrix[s1] = {};
            symbols.forEach(s2 => {
                if (s1 === s2) {
                    matrix[s1][s2] = 1.00;
                } else {
                    // Find correlation in data
                    const pair = riskData.correlationMatrix.find(
                        item => (item.symbol1 === s1 && item.symbol2 === s2) ||
                            (item.symbol1 === s2 && item.symbol2 === s1)
                    );
                    matrix[s1][s2] = pair ? pair.correlation : null;
                }
            });
        });

        return { symbols, matrix };
    };

    if (!riskData || riskData.error) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">
                    Unable to calculate risk metrics. Insufficient price data for your holdings.
                </Typography>
            </Box>
        );
    }

    // Explanation items for grid
    const explanations = [
        {
            label: `Alpha: ${riskData.alpha ?? '--'}%`,
            text: riskData.alpha > 0
                ? "Outperforming - generating excess returns for your risk level."
                : riskData.alpha < 0
                    ? "Underperforming - not being compensated enough for your risk."
                    : "Matching expected return for your risk level."
        },
        {
            label: `Beta: ${riskData.beta ?? '--'}`,
            text: riskData.beta < 0.8
                ? "Your portfolio is less volatile than the market - more defensive."
                : riskData.beta <= 1.2
                    ? "Your portfolio moves similarly to the market."
                    : "Your portfolio is more volatile than the market - higher risk/reward."
        },
        {
            label: `Sharpe Ratio: ${riskData.sharpe ?? '--'}`,
            text: riskData.sharpe < 0
                ? "Negative risk-adjusted return - underperforming risk-free rate."
                : riskData.sharpe < 1
                    ? "Modest risk-adjusted return."
                    : riskData.sharpe < 2
                        ? "Good risk-adjusted return - your risk is being rewarded."
                        : "Excellent risk-adjusted return!"
        },
        {
            label: `Max Drawdown: -${riskData.maxDrawdown ?? '--'}%`,
            text: riskData.maxDrawdown < 10
                ? "Low drawdown - your portfolio has been relatively stable."
                : riskData.maxDrawdown < 20
                    ? "Moderate drawdown - typical for a diversified portfolio."
                    : "Significant drawdown - consider if this matches your risk tolerance."
        }
    ];

    const { symbols: matrixSymbols, matrix } = buildCorrelationMatrix();

    return (
        <Box>
            {/* Risk Metrics Cards */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
                gap: 2,
                mb: 3,
                maxWidth: isMobile ? '100%' : 950,
                mx: 'auto',
                '& > :last-child': {
                    gridColumn: isMobile ? 'span 2' : 'auto',
                }
            }}>
                <MetricCard
                    label="Portfolio Beta"
                    value={riskData.beta ?? '--'}
                    tooltip="Measures volatility vs S&P 500. Beta = 1 means same as market. Higher = more volatile."
                    color={getBetaColor(riskData.beta)}
                />
                <MetricCard
                    label="Volatility"
                    value={riskData.volatility ?? '--'}
                    suffix="%"
                    tooltip="Annualized standard deviation. Shows how much your portfolio value typically swings per year."
                    color={getVolatilityColor(riskData.volatility)}
                />
                <MetricCard
                    label="Alpha"
                    value={riskData.alpha ?? '--'}
                    suffix="%"
                    tooltip="Excess return vs expected. Positive = outperforming for your risk level."
                    color={getAlphaColor(riskData.alpha)}
                />
                <MetricCard
                    label="Sharpe Ratio"
                    value={riskData.sharpe ?? '--'}
                    tooltip="Risk-adjusted return. Higher is better. Above 1 is good, above 2 is excellent."
                    color={getSharpeColor(riskData.sharpe)}
                />
                <MetricCard
                    label="Max Drawdown"
                    value={riskData.maxDrawdown ? `-${riskData.maxDrawdown}` : '--'}
                    suffix="%"
                    tooltip="Worst peak-to-trough decline in the period. Shows your worst-case historical loss."
                    color={getDrawdownColor(riskData.maxDrawdown)}
                />
            </Box>

            {/* Interpretation Guide */}
            <Box sx={{
                p: 2,
                bgcolor: theme.palette.background.default,
                borderRadius: 2,
                mb: 3
            }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ textAlign: 'center' }}>
                    What do these mean?
                </Typography>

                {/* Grid on desktop, stacked on mobile */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: 3,
                    maxWidth: 800,
                    mx: 'auto',
                    mt: 2
                }}>
                    {explanations.map((item, index) => (
                        <Box key={index} sx={{ textAlign: isMobile ? 'left' : 'center' }}>
                            <Typography variant="body2" fontWeight="medium">
                                {item.label}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {item.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Coverage and data info - centered */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    {riskData.coverage && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {riskData.coverage.includedPositions === riskData.coverage.totalPositions ? (
                                <>✓ 100% of your portfolio value has impact data</>
                            ) : (
                                <>
                                    ⚠ {riskData.coverage.excludedSymbols.length} of {riskData.coverage.totalPositions} positions
                                    ({riskData.coverage.excludedSymbols.join(', ')}) have no data and have been excluded
                                </>
                            )}
                        </Typography>
                    )}

                    {riskData.dataPoints && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            Based on {riskData.dataPoints} trading days of data
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Correlation Matrix - Grid Style */}
            {matrixSymbols.length >= 2 && (
                <Box sx={{
                    p: 2,
                    bgcolor: theme.palette.background.default,
                    borderRadius: 2
                }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ textAlign: 'center' }}>
                        Correlation Matrix
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2, textAlign: 'center' }}>
                        1 = move together, 0 = independent
                    </Typography>

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: matrixSymbols.length * 70 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none' }}></TableCell>
                                    {matrixSymbols.map(symbol => (
                                        <TableCell
                                            key={symbol}
                                            align="center"
                                            sx={{
                                                fontWeight: 'bold',
                                                borderBottom: 'none',
                                                fontSize: '0.75rem',
                                                px: 1
                                            }}
                                        >
                                            {symbol}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matrixSymbols.map(rowSymbol => (
                                    <TableRow key={rowSymbol}>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                borderBottom: 'none',
                                                fontSize: '0.75rem',
                                                px: 1
                                            }}
                                        >
                                            {rowSymbol}
                                        </TableCell>
                                        {matrixSymbols.map(colSymbol => {
                                            const corr = matrix[rowSymbol]?.[colSymbol];
                                            return (
                                                <TableCell
                                                    key={colSymbol}
                                                    align="center"
                                                    sx={{
                                                        bgcolor: getCorrelationBgColor(corr),
                                                        color: getCorrelationTextColor(corr),
                                                        fontWeight: corr === 1 ? 'normal' : 'medium',
                                                        borderBottom: 'none',
                                                        fontSize: '0.75rem',
                                                        px: 1,
                                                        py: 0.75
                                                    }}
                                                >
                                                    {corr !== null ? corr.toFixed(2) : '--'}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="textSecondary">
                            🟢 &lt;0.4 Good
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            🟡 0.4-0.7 Moderate
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            🔴 &gt;0.7 High (less diversified)
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PortfolioInsightsRisk;